/*
================================================================================
FILE HEADER — apps/studio-web/src/components/LeftNav.tsx
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                  | What Changed           |
|----|------------|-------------------|----------------------|------------------------|
| 1  | 2026-06-25 | Claude Sonnet 4.6 | Mimiq layout match   | Collapsible sidebar    |
|    |            |                   |                      | with labels            |
| 2  | 2026-06-25 | Claude Sonnet 4.6 | Execution + Reports  | Wire Execution and     |
|    |            |                   |                      | Reports as real tabs   |
| 3  | 2026-07-02 | Claude Sonnet 4.6 | Functions tab        | Wire Functions as real |
| 4  | 2026-11-05 | Claude Opus 5.5   | Public hosting + rename | Visible name Plex → Scriptwave; hosted at scriptwave.qagreen.com |
|    |            |                   |                      | tab (remove stub)      |

EXPORTS COVERED
---------------
- LeftNav — collapsible sidebar: logo, nav items, collapse arrow
================================================================================
*/

import { useState } from 'react';
import type { StudioTab } from '../App.js';

type NavItem = {
  icon: string;
  label: string;
  tab?: StudioTab;
  stub?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  { icon: '⏺',  label: 'Record',     tab: 'record'    },
  { icon: '☰',  label: 'Test Cases', tab: 'tests'     },
  { icon: '▶',  label: 'Execution',  tab: 'execution' },
  { icon: '📊', label: 'Reports',    tab: 'reports'   },
];

const BOTTOM_ITEMS: NavItem[] = [
  { icon: '⚙',  label: 'Functions',  tab: 'functions' },
  { icon: '?',  label: 'Wiki / Docs', stub: true },
  { icon: '⚙',  label: 'Settings',   tab: 'settings' },
];

type Props = {
  activeTab: StudioTab;
  onTabChange: (tab: StudioTab) => void;
};

export function LeftNav({ activeTab, onTabChange }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const W = collapsed ? 52 : 196;

  return (
    <nav style={{
      width: W, flexShrink: 0,
      borderRight: '1px solid #27272a',
      display: 'flex', flexDirection: 'column',
      background: '#0d0d10',
      transition: 'width 0.2s ease',
      overflow: 'hidden',
    }}>
      {/* Logo */}
      <div style={{
        padding: collapsed ? '14px 0' : '14px 16px',
        borderBottom: '1px solid #27272a',
        display: 'flex', alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}>
        <span style={{ fontWeight: 800, fontSize: 16, color: '#a78bfa', whiteSpace: 'nowrap' }}>
          {collapsed ? 'S' : 'Scriptwave'}
        </span>
      </div>

      {/* Top nav items */}
      <div style={{ flex: 1, paddingTop: 8 }}>
        {NAV_ITEMS.map((item) => (
          <NavButton
            key={item.label}
            item={item}
            active={item.tab === activeTab}
            collapsed={collapsed}
            onClick={() => item.tab && onTabChange(item.tab)}
          />
        ))}
      </div>

      {/* Bottom nav items */}
      <div style={{ borderTop: '1px solid #1c1c1f', paddingTop: 4, paddingBottom: 4 }}>
        {BOTTOM_ITEMS.map((item) => (
          <NavButton
            key={item.label}
            item={item}
            active={item.tab === activeTab}
            collapsed={collapsed}
            onClick={() => item.tab && onTabChange(item.tab)}
          />
        ))}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed((v) => !v)}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        style={{
          background: 'none', border: 'none', borderTop: '1px solid #27272a',
          color: '#52525b', cursor: 'pointer', padding: '10px',
          display: 'flex', alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-end',
          fontSize: 14,
        }}
      >
        {collapsed ? '›' : '‹'}
      </button>
    </nav>
  );
}

function NavButton({ item, active, collapsed, onClick }: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);

  return (
    <button
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      disabled={item.stub}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        display: 'flex', alignItems: 'center',
        gap: 10,
        padding: collapsed ? '10px 0' : '9px 14px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        background: active ? 'rgba(124,58,237,0.2)' : hover && !item.stub ? 'rgba(255,255,255,0.04)' : 'none',
        border: 'none',
        borderLeft: active ? '3px solid #7c3aed' : '3px solid transparent',
        color: active ? '#c4b5fd' : item.stub ? '#3f3f46' : '#a1a1aa',
        cursor: item.stub ? 'not-allowed' : 'pointer',
        fontSize: 13,
        textAlign: 'left',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        transition: 'background 0.1s',
      }}
    >
      <span style={{ fontSize: 15, flexShrink: 0 }}>{item.icon}</span>
      {!collapsed && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>}
    </button>
  );
}
